/**
 * Project Store
 *
 * Manages current project state, categories, images, and members.
 * Updated via realtime broadcast events for real-time sync.
 */

import { create } from 'zustand';
import { loadCreditPacks } from '../services/creditPacksCache.js';
import { getProject as grpcGetProject } from '../services/visionProjectsClient.js';
import { listProjectImages } from '../services/visionAssetsClient.js';
import { listVisionInvites, listVisionMembers } from '../services/visionMembershipClient.js';
import { deriveImageFilenames } from '../utils/imageFilename.js';

// ─── localStorage project cache (instant hydration on page refresh) ──────────
const PROJECT_CACHE_KEY = 'rrecce_project_cache';
let imageRefreshEpoch = 0;
let imageRefreshRevision = 0;
let imageRefreshRequest = null;

function getCachedProject() {
  try {
    const raw = localStorage.getItem(PROJECT_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    // Only hydrate if the cached project matches the current URL
    const match = window.location.pathname.match(/\/project\/([^/]+)/);
    if (match && cached?.id === match[1]) return cached;
    return null;
  } catch { return null; }
}

function setCachedProject(project) {
  try {
    if (project) {
      // Cache only the display-essential fields
      localStorage.setItem(PROJECT_CACHE_KEY, JSON.stringify({
        id: project.id,
        name: project.name,
        user_role: project.user_role,
        project_credits: project.project_credits,
      }));
    } else {
      localStorage.removeItem(PROJECT_CACHE_KEY);
    }
  } catch { /* non-critical */ }
}

const cachedProject = getCachedProject();

export const useProjectStore = create((set, get) => ({
  currentProject: cachedProject,
  categories: [],
  images: [],
  members: [],
  pendingInvites: [],
  membersLoaded: false, // Track if members have been fetched for current project
  isLoading: false,
  isLoaded: false, // true after a full API load (false for localStorage cache shell)

  // Cached artboard data (persists across page navigations within project)
  shotsByImageId: {},       // { imageId: shot }
  shotPresetsCache: null,   // { frameSize: [], angle: [], ... } or null if not loaded
  chatCountsCache: null,    // { imageId: count } or null if not loaded
  artboardDataLoaded: false, // true once artboard data has been fetched/bootstrapped for this project
  setShotsByImageId: (updater) => {
    set((state) => ({
      shotsByImageId: typeof updater === 'function' ? updater(state.shotsByImageId) : updater,
    }));
  },
  setShotPresetsCache: (presets) => set({ shotPresetsCache: presets }),
  setChatCountsCache: (counts) => set({ chatCountsCache: counts }),

  /**
   * Load a project and its core data (project, categories, images).
   * Pass include='artboard' to also fetch shots, presets, and chat counts in the same request.
   * Members are lazy-loaded separately via loadMembers() to avoid
   * blocking pages that don't need them (e.g. GenerationPage).
   * @param {string} projectId
   * @param {object} [options]
   * @param {string} [options.include] - 'artboard' to bundle artboard data
   */
  loadProject: async (projectId, { include } = {}) => {
    const loadEpoch = ++imageRefreshEpoch;
    const imageRevision = imageRefreshRevision;
    set({ isLoading: true });
    try {
      const data = await grpcGetProject({ projectId, include });
      const { project, categories, user_role, project_credits } = data;

      // GetVisionProject does not carry images; fetch them via the assets path.
      const images = await listProjectImages(projectId);
      if (loadEpoch !== imageRefreshEpoch) return;
      const currentImages = imageRevision !== imageRefreshRevision && get().currentProject?.id === projectId
        ? get().images : images;

      const update = {
        currentProject: { ...project, user_role, project_credits: project_credits || project.project_credits || 0 },
        categories,
        images: deriveImageFilenames(currentImages, categories),
        isLoading: false,
        isLoaded: true,
        // Reset members when switching projects
        members: [],
        pendingInvites: [],
        membersLoaded: false,
        // Reset caches when switching projects
        shotsByImageId: {},
        shotPresetsCache: null,
        chatCountsCache: null,
        artboardDataLoaded: false,
      };

      // Populate caches if artboard data was included in bootstrap
      if (data.shots) {
        const map = {};
        data.shots.forEach(s => { if (s.image_id) map[s.image_id] = s; });
        update.shotsByImageId = map;
        update.artboardDataLoaded = true;
      }
      if (data.shotPresets) {
        update.shotPresetsCache = data.shotPresets; // already merged (built-in + custom)
      }
      if (data.chatCounts) {
        update.chatCountsCache = data.chatCounts;
      }

      setCachedProject(update.currentProject);
      set(update);

      // Pre-fetch credit packs in background (non-blocking)
      loadCreditPacks();
    } catch (error) {
      console.error('[ProjectStore] Load error:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  refreshImages: () => {
    const projectId = get().currentProject?.id;
    if (!projectId) return Promise.resolve();
    if (imageRefreshRequest?.projectId === projectId && imageRefreshRequest.epoch === imageRefreshEpoch) {
      imageRefreshRequest.again = true;
      return imageRefreshRequest.promise;
    }
    const request = { projectId, epoch: imageRefreshEpoch, again: false };
    imageRefreshRequest = request;
    request.promise = (async () => {
      do {
        request.again = false;
        const images = await listProjectImages(projectId);
        if (request.epoch !== imageRefreshEpoch || get().currentProject?.id !== projectId) return;
        if (!request.again) {
          imageRefreshRevision += 1;
          set(state => ({ images: deriveImageFilenames(images, state.categories) }));
        }
      } while (request.again);
    })().finally(() => {
      if (imageRefreshRequest === request) imageRefreshRequest = null;
    });
    return request.promise;
  },

  /**
   * Lazy-load members and pending invites for the current project.
   * No-op if already loaded. Call from components that need member data (TeamPopup, etc.).
   */
  loadMembers: async () => {
    const state = get();
    if (state.membersLoaded || !state.currentProject) return;
    const projectId = state.currentProject.id;

    // Members come from ListVisionMembers and pending invites from ListVisionInvites; each is
    // loaded independently so one failing does not block the other.
    let members = [];
    let pendingInvites = [];
    try {
      members = await listVisionMembers(projectId);
    } catch (error) {
      console.error('[ProjectStore] Load members error:', error);
    }
    try {
      pendingInvites = await listVisionInvites(projectId);
    } catch (error) {
      console.error('[ProjectStore] Load invites error:', error);
    }
    set({ members, pendingInvites, membersLoaded: true });
  },

  /**
   * Clear current project state
   */
  clearProject: () => {
    imageRefreshEpoch += 1;
    setCachedProject(null);
    set({
      currentProject: null,
      categories: [],
      images: [],
      members: [],
      pendingInvites: [],
      membersLoaded: false,
      isLoaded: false,
      shotsByImageId: {},
      shotPresetsCache: null,
      chatCountsCache: null,
      artboardDataLoaded: false,
    });
  },

  /**
   * Update project details
   * @param {object} updates
   */
  updateProject: (updates) => {
    set((state) => {
      const merged = state.currentProject
        ? { ...state.currentProject, ...updates }
        : null;
      setCachedProject(merged);
      return { currentProject: merged };
    });
  },

  /**
   * Update project credit balance (from credits-updated realtime event).
   * Only updates if projectId matches the current project to prevent stale broadcasts
   * from overwriting the wrong project's balance.
   * @param {string} projectId
   * @param {number} balance
   */
  updateProjectCredits: (projectId, balance) => {
    set((state) => {
      const updated = state.currentProject?.id === projectId
        ? { ...state.currentProject, project_credits: balance }
        : state.currentProject;
      if (updated !== state.currentProject) setCachedProject(updated);
      return { currentProject: updated };
    });
  },

  /**
   * Add a new category
   * @param {object} category
   */
  addCategory: (category) => {
    set((state) => {
      // Prevent duplicates (API response + socket event may both fire)
      if (state.categories.some(c => c.id === category.id)) {
        return state;
      }
      const categories = [...state.categories, category];
      return { categories, images: deriveImageFilenames(state.images, categories) };
    });
  },

  /**
   * Update category
   * @param {string} categoryId
   * @param {object} updates
   */
  updateCategory: (categoryId, updates) => {
    set((state) => {
      const categories = state.categories.map((cat) =>
        cat.id === categoryId ? { ...cat, ...updates } : cat
      );
      return { categories, images: deriveImageFilenames(state.images, categories) };
    });
  },

  /**
   * Remove category
   * @param {string} categoryId
   */
  removeCategory: (categoryId) => {
    set((state) => {
      const categories = state.categories.filter((cat) => cat.id !== categoryId);
      return { categories, images: deriveImageFilenames(state.images, categories) };
    });
  },

  /**
   * Add a new image
   * @param {object} image
   */
  addImage: (image) => {
    set((state) => {
      // Prevent duplicates (API response + socket event may both fire)
      if (state.images.some(img => img.id === image.id)) {
        return state;
      }
      return { images: deriveImageFilenames([...state.images, image], state.categories) };
    });
  },

  /**
   * Update image
   * @param {string} imageId
   * @param {object} updates
   */
  updateImage: (imageId, updates) => {
    set((state) => ({
      images: deriveImageFilenames(
        state.images.map((img) => (img.id === imageId ? { ...img, ...updates } : img)),
        state.categories,
      ),
    }));
  },

  /**
   * Remove image
   * @param {string} imageId
   */
  removeImage: (imageId) => {
    set((state) => ({
      images: deriveImageFilenames(
        state.images.filter((img) => img.id !== imageId),
        state.categories,
      ),
    }));
  },

  /**
   * Move image to a different category.
   * @param {string} imageId
   * @param {string} newCategoryId
   */
  moveImage: (imageId, newCategoryId) => {
    set((state) => ({
      images: deriveImageFilenames(
        state.images.map((img) =>
          img.id === imageId ? { ...img, category_id: newCategoryId, sort_order: 0 } : img
        ),
        state.categories,
      ),
    }));
  },

  /**
   * Reorder images within a category
   * @param {string[]} imageIds - new order with updated sort_order
   */
  reorderImages: (imageIds) => {
    set((state) => {
      // Update sort_order for reordered images
      const updatedImages = state.images.map((img) => {
        const newIndex = imageIds.indexOf(img.id);
        if (newIndex !== -1) {
          return { ...img, sort_order: newIndex };
        }
        return img;
      });
      return { images: deriveImageFilenames(updatedImages, state.categories) };
    });
  },

  /**
   * Add a team member
   * @param {object} member
   */
  addMember: (member) => {
    set((state) => {
      // Prevent duplicate entries (API response + socket event can both trigger this)
      const userId = member.user_id;
      if (userId && state.members.some((m) => m.user_id === userId)) {
        return state;
      }
      return { members: [...state.members, member] };
    });
  },

  /**
   * Update member role
   * @param {string} userId
   * @param {string} role
   */
  updateMemberRole: (userId, role) => {
    set((state) => {
      const members = state.members.map((member) =>
        member.user_id === userId ? { ...member, role } : member
      );
      // When the changed member is the current user, mirror the new role into
      // currentProject.user_role — the role-gated UI (sidebar settings gear, invite
      // controls) reads user_role, not the members list, so it stays stale otherwise.
      const changed = members.find((member) => member.user_id === userId);
      if (changed?.is_me && state.currentProject) {
        const currentProject = { ...state.currentProject, user_role: role };
        setCachedProject(currentProject);
        return { members, currentProject };
      }
      return { members };
    });
  },

  /**
   * Remove member
   * @param {string} userId
   */
  removeMember: (userId) => {
    set((state) => ({
      members: state.members.filter((member) => member.user_id !== userId),
    }));
  },

  /**
   * Add a pending invite
   * @param {object} invite
   */
  addPendingInvite: (invite) => {
    set((state) => {
      if (state.pendingInvites.some((i) => i.id === invite.id)) return state;
      return { pendingInvites: [...state.pendingInvites, invite] };
    });
  },

  /**
   * Remove a pending invite
   * @param {string} inviteId
   */
  removePendingInvite: (inviteId) => {
    set((state) => ({
      pendingInvites: state.pendingInvites.filter((i) => i.id !== inviteId),
    }));
  },
}));
